import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// API BASE
// ============================================================

const BASE_URL =
  "/api/v1/sales-executives";

// ============================================================
// ERROR HELPER
// ============================================================

const getErrorMessage = (
  error,
  fallback = "Something went wrong"
) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

// ============================================================
// 1) FETCH LIST
//
// GET /api/v1/sales-executives
//
// Supports:
// page
// page_size
// search
// emp_code
// mobile_no
// name
// status
// joined_from
// joined_to
// sort_by
// sort_order
// ============================================================

export const fetchSalesExecutivesThunk =
  createAsyncThunk(
    "salesExecutives/fetchList",

    async (
      filters = {},
      { rejectWithValue }
    ) => {
      try {
        const params = {
          page:
            Number(filters.page) || 1,

          page_size:
            Number(
              filters.page_size
            ) || 20,

          search:
            filters.search || "",

          emp_code:
            filters.emp_code || "",

          mobile_no:
            filters.mobile_no || "",

          name:
            filters.name || "",

          status:
            filters.status || "",

          joined_from:
            filters.joined_from || "",

          joined_to:
            filters.joined_to || "",

          sort_by:
            filters.sort_by ||
            "sales_executive_id",

          sort_order:
            filters.sort_order ||
            "desc",
        };

        // Remove empty values
        Object.keys(params).forEach(
          (key) => {
            if (
              params[key] === "" ||
              params[key] === null ||
              params[key] === undefined
            ) {
              delete params[key];
            }
          }
        );

        const response =
          await api.get(
            BASE_URL,
            {
              params,
            }
          );

        if (
          response?.data?.success ===
          false
        ) {
          return rejectWithValue(
            response.data.message ||
              "Failed to load sales executives"
          );
        }

        return {
          list:
            response?.data?.data ||
            [],

          pagination:
            response?.data
              ?.pagination || {
              total: 0,
              page:
                Number(
                  filters.page
                ) || 1,
              page_size:
                Number(
                  filters.page_size
                ) || 20,
              total_pages: 0,
            },
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load sales executives"
          )
        );
      }
    }
  );

// ============================================================
// 2) LOOKUP
//
// GET /api/v1/sales-executives/lookup
//
// For dropdowns
// ============================================================

export const fetchSalesExecutiveLookupThunk =
  createAsyncThunk(
    "salesExecutives/fetchLookup",

    async (
      filters = {},
      { rejectWithValue }
    ) => {
      try {
        const params = {
          search:
            filters.search || "",

          status:
            filters.status ||
            "Active",
        };

        Object.keys(params).forEach(
          (key) => {
            if (
              params[key] === "" ||
              params[key] === null ||
              params[key] === undefined
            ) {
              delete params[key];
            }
          }
        );

        const response =
          await api.get(
            `${BASE_URL}/lookup`,
            {
              params,
            }
          );

        if (
          response?.data?.success ===
          false
        ) {
          return rejectWithValue(
            response.data.message ||
              "Failed to load sales executive lookup"
          );
        }

        return (
          response?.data?.data ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load sales executive lookup"
          )
        );
      }
    }
  );

// ============================================================
// 3) GET BY ID
//
// GET /api/v1/sales-executives/:id
// ============================================================

export const fetchSalesExecutiveByIdThunk =
  createAsyncThunk(
    "salesExecutives/fetchById",

    async (
      sales_executive_id,
      { rejectWithValue }
    ) => {
      try {
        if (
          !sales_executive_id
        ) {
          return rejectWithValue(
            "sales_executive_id required"
          );
        }

        const response =
          await api.get(
            `${BASE_URL}/${sales_executive_id}`
          );

        if (
          response?.data?.success ===
          false
        ) {
          return rejectWithValue(
            response.data.message ||
              "Sales executive not found"
          );
        }

        return (
          response?.data?.data ||
          null
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load sales executive"
          )
        );
      }
    }
  );

// ============================================================
// 4) CREATE
//
// POST /api/v1/sales-executives
// ============================================================

export const createSalesExecutiveThunk =
  createAsyncThunk(
    "salesExecutives/create",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const response =
          await api.post(
            BASE_URL,
            payload
          );

        if (
          response?.data?.success ===
          false
        ) {
          return rejectWithValue(
            response.data.message ||
              "Failed to create sales executive"
          );
        }

        return response.data;
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to create sales executive"
          )
        );
      }
    }
  );

// ============================================================
// 5) UPDATE
//
// PUT /api/v1/sales-executives/:id
// ============================================================

export const updateSalesExecutiveThunk =
  createAsyncThunk(
    "salesExecutives/update",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const {
          sales_executive_id,
          ...body
        } = payload || {};

        if (
          !sales_executive_id
        ) {
          return rejectWithValue(
            "sales_executive_id required"
          );
        }

        const response =
          await api.put(
            `${BASE_URL}/${sales_executive_id}`,
            body
          );

        if (
          response?.data?.success ===
          false
        ) {
          return rejectWithValue(
            response.data.message ||
              "Failed to update sales executive"
          );
        }

        return {
          sales_executive_id,
          ...body,
          ...(response?.data
            ?.data || {}),
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update sales executive"
          )
        );
      }
    }
  );

// ============================================================
// 6) DELETE
//
// DELETE /api/v1/sales-executives/:id
// ============================================================

export const deleteSalesExecutiveThunk =
  createAsyncThunk(
    "salesExecutives/delete",

    async (
      {
        sales_executive_id,
      },
      { rejectWithValue }
    ) => {
      try {
        if (
          !sales_executive_id
        ) {
          return rejectWithValue(
            "sales_executive_id required"
          );
        }

        const response =
          await api.delete(
            `${BASE_URL}/${sales_executive_id}`
          );

        if (
          response?.data?.success ===
          false
        ) {
          return rejectWithValue(
            response.data.message ||
              "Failed to delete sales executive"
          );
        }

        return {
          sales_executive_id,
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to delete sales executive"
          )
        );
      }
    }
  );

// ============================================================
// INITIAL STATE
// ============================================================

const initialState = {
  list: [],

  lookup: [],

  selected: null,

  pagination: {
    total: 0,
    page: 1,
    page_size: 20,
    total_pages: 0,
  },

  loading: false,

  lookupLoading: false,

  saving: false,

  deleting: false,

  error: null,
};

// ============================================================
// SLICE
// ============================================================

const salesExecutiveSlice =
  createSlice({
    name: "salesExecutives",

    initialState,

    reducers: {
      clearSalesExecutiveError:
        (state) => {
          state.error = null;
        },

      clearSalesExecutives:
        (state) => {
          state.list = [];

          state.pagination = {
            total: 0,
            page: 1,
            page_size: 20,
            total_pages: 0,
          };

          state.error = null;
        },

      clearSalesExecutiveLookup:
        (state) => {
          state.lookup = [];
        },

      clearSelectedSalesExecutive:
        (state) => {
          state.selected = null;
        },
    },

    extraReducers: (
      builder
    ) => {
      // ======================================================
      // FETCH LIST
      // ======================================================

      builder
        .addCase(
          fetchSalesExecutivesThunk.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchSalesExecutivesThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.list =
              action.payload
                ?.list || [];

            state.pagination =
              action.payload
                ?.pagination || {
                total: 0,
                page: 1,
                page_size: 20,
                total_pages: 0,
              };
          }
        )

        .addCase(
          fetchSalesExecutivesThunk.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Failed to load sales executives";
          }
        );

      // ======================================================
      // LOOKUP
      // ======================================================

      builder
        .addCase(
          fetchSalesExecutiveLookupThunk.pending,
          (state) => {
            state.lookupLoading =
              true;
          }
        )

        .addCase(
          fetchSalesExecutiveLookupThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.lookupLoading =
              false;

            state.lookup =
              action.payload ||
              [];
          }
        )

        .addCase(
          fetchSalesExecutiveLookupThunk.rejected,
          (
            state,
            action
          ) => {
            state.lookupLoading =
              false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Failed to load sales executive lookup";
          }
        );

      // ======================================================
      // GET BY ID
      // ======================================================

      builder
        .addCase(
          fetchSalesExecutiveByIdThunk.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchSalesExecutiveByIdThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.selected =
              action.payload ||
              null;
          }
        )

        .addCase(
          fetchSalesExecutiveByIdThunk.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Failed to load sales executive";
          }
        );

      // ======================================================
      // CREATE
      // ======================================================

      builder
        .addCase(
          createSalesExecutiveThunk.pending,
          (state) => {
            state.saving = true;
            state.error = null;
          }
        )

        .addCase(
          createSalesExecutiveThunk.fulfilled,
          (state) => {
            state.saving = false;
          }
        )

        .addCase(
          createSalesExecutiveThunk.rejected,
          (
            state,
            action
          ) => {
            state.saving = false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Failed to create sales executive";
          }
        );

      // ======================================================
      // UPDATE
      // ======================================================

      builder
        .addCase(
          updateSalesExecutiveThunk.pending,
          (state) => {
            state.saving = true;
            state.error = null;
          }
        )

        .addCase(
          updateSalesExecutiveThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.saving = false;

            const id =
              Number(
                action.payload
                  ?.sales_executive_id
              );

            const index =
              state.list.findIndex(
                (row) =>
                  Number(
                    row.sales_executive_id
                  ) === id
              );

            if (index >= 0) {
              state.list[index] = {
                ...state.list[index],
                ...action.payload,
              };
            }

            if (
              Number(
                state.selected
                  ?.sales_executive_id
              ) === id
            ) {
              state.selected = {
                ...state.selected,
                ...action.payload,
              };
            }
          }
        )

        .addCase(
          updateSalesExecutiveThunk.rejected,
          (
            state,
            action
          ) => {
            state.saving = false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Failed to update sales executive";
          }
        );

      // ======================================================
      // DELETE
      // ======================================================

      builder
        .addCase(
          deleteSalesExecutiveThunk.pending,
          (state) => {
            state.deleting = true;
            state.error = null;
          }
        )

        .addCase(
          deleteSalesExecutiveThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.deleting = false;

            const id =
              Number(
                action.payload
                  ?.sales_executive_id
              );

            state.list =
              state.list.filter(
                (row) =>
                  Number(
                    row.sales_executive_id
                  ) !== id
              );

            if (
              Number(
                state.selected
                  ?.sales_executive_id
              ) === id
            ) {
              state.selected = null;
            }
          }
        )

        .addCase(
          deleteSalesExecutiveThunk.rejected,
          (
            state,
            action
          ) => {
            state.deleting = false;

            state.error =
              action.payload ||
              action.error
                ?.message ||
              "Failed to delete sales executive";
          }
        );
    },
  });

// ============================================================
// EXPORT ACTIONS
// ============================================================

export const {
  clearSalesExecutiveError,
  clearSalesExecutives,
  clearSalesExecutiveLookup,
  clearSelectedSalesExecutive,
} = salesExecutiveSlice.actions;

// ============================================================
// EXPORT REDUCER
// ============================================================

export default salesExecutiveSlice.reducer;